import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { ChartType } from '@/models/chart'
import {
  buildMonitorChartData,
  MonitorItemData,
} from '@/pages/instance-list/components/MonitorChart'
import { useTeamStore } from '@/stores/team'
import { getToken as getPlatformToken } from '@/utils/auth'

interface UseInstanceMonitorDataParams {
  instance: any
  filterDuration: number
}

function normalizeBearer(token?: string | null): string | null {
  if (!token) return null
  return token.startsWith('Bearer ') ? token : `Bearer ${token}`
}

function getCurrentUserBearer(): string | null {
  const teamRaw = useTeamStore.getState().teamTokenData?.token
  const teamBearer = normalizeBearer(teamRaw)
  if (teamBearer) return teamBearer

  const platformRaw = getPlatformToken()
  const platformBearer = normalizeBearer(platformRaw)
  if (platformBearer) return platformBearer

  return null
}

export function useInstanceMonitorData({ instance, filterDuration }: UseInstanceMonitorDataParams) {
  const { t } = useTranslation()
  const [monitorDatas, setMonitorDatas] = useState<MonitorItemData[]>([])
  const [initialLoading, setInitialLoading] = useState(true)
  const [grantPending, setGrantPending] = useState(false)
  const [monitorBearer, setMonitorBearer] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  const teamTokenData = useTeamStore((state) => state.teamTokenData)
  const isTeamContext = !!teamTokenData

  const instanceIdNum = Number(
    (instance as any).id ?? (instance as any).realID ?? instance?.displayID,
  )

  // Grant token for team context
  useEffect(() => {
    if (!isTeamContext) {
      // Platform context, no need to grant
      setMonitorBearer(null)
      setGrantPending(false)
      return
    }

    let mounted = true
    ;(async () => {
      setGrantPending(true)
      const fallbackBearer = getCurrentUserBearer()

      if (!Number.isFinite(instanceIdNum)) {
        if (mounted) {
          setMonitorBearer(fallbackBearer)
          setGrantPending(false)
        }
        return
      }

      try {
        const res = await API.grantNodeMetricsAccess({
          privilege_type: 65,
          source_id: instanceIdNum,
        })
        if (mounted) {
          setMonitorBearer(res?.code === 0 && res?.token ? res.token : fallbackBearer)
          setGrantPending(false)
        }
      } catch {
        if (mounted) {
          setMonitorBearer(fallbackBearer)
          setGrantPending(false)
        }
      }
    })()

    return () => {
      mounted = false
    }
  }, [isTeamContext, instanceIdNum])

  useEffect(() => {
    if (!instance?.displayID) {
      setInitialLoading(false)
      setRefreshing(false)
      return
    }

    if (isTeamContext && !monitorBearer) {
      return
    }

    // Each range owns its requests; a previous range cannot block or overwrite it.
    const controller = new AbortController()
    let fetching = false
    setRefreshing(true)

    const fetchDetailMonitorData = async () => {
      if (fetching) return
      fetching = true

      try {
        const params = {
          chart_type: [
            ChartType.GPUCore,
            ChartType.GPURAM,
            ChartType.CPU,
            ChartType.Memory,
            ChartType.Disk,
          ],
          request_id: instance.displayID,
          time_interval_in_sec: filterDuration,
        }
        const config = { signal: controller.signal }
        const res =
          isTeamContext && monitorBearer
            ? await API._getDetailMonitorDataWithToken(params, monitorBearer, config)
            : await API.getDetailMonitorData(params, config)

        if (!controller.signal.aborted && res.code === 0) {
          setMonitorDatas(buildMonitorChartData(t, instance, res.data || []))
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Failed to fetch monitor data:', error)
        }
      } finally {
        fetching = false
        if (!controller.signal.aborted) {
          setInitialLoading(false)
          setRefreshing(false)
        }
      }
    }

    fetchDetailMonitorData()
    const timer = setInterval(fetchDetailMonitorData, 30000)

    return () => {
      controller.abort()
      clearInterval(timer)
    }
  }, [instance, isTeamContext, monitorBearer, filterDuration, t])

  return {
    monitorDatas,
    initialLoading: grantPending || initialLoading,
    refreshing,
  }
}
